import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Receipt, RefreshCw, Eye, X, Search, Globe, Monitor, Shield, Trash2, AlertTriangle, ExternalLink, FileText, History, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { getKioskToken, KIOSK_API_BASE, KIOSK_TRANSACTIONS_URL } from '../../lib/kioskTokenManager';

// API endpoints
const BACKEND_API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_API_BASE}/api`;

// Kiosk API endpoints are imported from shared kioskTokenManager

// Helper function to format date from yyyy-mm-dd to mm/dd/yyyy
const formatDateMMDDYYYY = (dateString) => {
  if (!dateString) return '-';
  const parts = dateString.split('-');
  if (parts.length === 3) {
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }
  return dateString;
};

// Format time for display in PHP-style `h:mm am/pm` (e.g. "8:59 am")
// Accepts: "13:00:00", "13:00", "10AM", "2PM", "08:30 AM"
const formatKioskTimeDisplay = (timeStr) => {
  if (!timeStr) return '';

  const raw = String(timeStr).trim();

  // 24-hour formats: HH:MM or HH:MM:SS
  const match24 = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (match24) {
    let hour24 = parseInt(match24[1], 10);
    const minutes = match24[2];
    if (!Number.isFinite(hour24)) hour24 = 0;
    const ampm = hour24 >= 12 ? 'pm' : 'am';
    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    return `${hour12}:${minutes} ${ampm}`;
  }

  // AM/PM formats: 10AM, 2PM, 08:30 AM
  const matchAmPm = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (matchAmPm) {
    let hour = parseInt(matchAmPm[1], 10);
    const minutes = matchAmPm[2] || '00';
    const ampm = matchAmPm[3].toLowerCase();
    return `${hour}:${minutes} ${ampm}`;
  }

  // Fallback: if it's something unexpected, try to at least show the hour
  const hour = parseInt(raw) || 0;
  const ampm = hour >= 12 ? 'pm' : 'am';
  let hour12 = hour % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12}:00 ${ampm}`;
};

const formatBookingSummary = (bookingInfos) => {
  const infos = Array.isArray(bookingInfos) ? bookingInfos : [];
  if (infos.length === 0) {
    return { courts: ['-'], dates: ['-'], times: ['-'], players: ['-'] };
  }

  const courts = infos.map((info) => (info?.court_no != null ? `Court ${info.court_no}` : info?.court || '-'));
  const dates = infos.map((info) => info?.booking_date ? formatDateMMDDYYYY(info.booking_date) : '-');
  const times = infos.map((info) => {
    if (info?.booking_time_start && info?.booking_time_end) {
      return `${formatKioskTimeDisplay(info.booking_time_start)} - ${formatKioskTimeDisplay(info.booking_time_end)}`;
    }
    return info?.time_slot ? formatKioskTimeDisplay(info.time_slot) : '-';
  });
  const players = infos.map((info) => {
    if (typeof info?.player_count === 'number') return String(info.player_count);
    if (typeof info?.num_players === 'number') return String(info.num_players);
    if (typeof info?.players === 'number') return String(info.players);
    return '-';
  });

  return { courts, dates, times, players };
};

const mapBookingToAdminTransaction = (booking) => {
  if (!booking) return null;
  const courtLabel =
    booking.court_name || booking.court_label || booking.court || booking.court_id || 'Court';
  const parsedCourtNo = String(booking.court_id || '').match(/(\d+)/);
  const startTime = booking.start_time || booking.time_slots?.[0] || '';
  let endTime = booking.end_time;
  if (!endTime) {
    const lastSlot = booking.time_slots?.[booking.time_slots.length - 1];
    if (lastSlot) {
      const hour = parseInt(lastSlot.split(':')[0], 10) || 0;
      endTime = `${String(hour + 1).padStart(2, '0')}:00`;
    }
  }

  // Determine if the booking is expired (past date + unpaid/pending)
  const bookingDate = booking.date;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const isPastDate = bookingDate && bookingDate < today;
  const isUnpaid = ['unpaid', 'pending', 'reserved'].includes(String(booking.payment_status || '').toLowerCase());
  const isCancelled = String(booking.status || '').toLowerCase() === 'cancelled';
  const isExpired = isPastDate && isUnpaid && !isCancelled;

  // Determine effective payment status
  let effectivePaymentStatus = booking.payment_status || (booking.transaction_type === 'reservation' ? 'unpaid' : 'paid');
  if (isCancelled) {
    effectivePaymentStatus = 'cancelled';
  } else if (isExpired) {
    effectivePaymentStatus = 'expired';
  }

  const bookingInfo = {
    court: courtLabel,
    court_no: parsedCourtNo ? Number(parsedCourtNo[1]) : undefined,
    booking_date: booking.date,
    booking_time_start: startTime,
    booking_time_end: endTime,
    time_slot: startTime,
    player_count:
      booking.num_players || booking.player_count || booking.players?.length || booking.max_players || booking.player_limit || '-',
  };

  return {
    source: 'admin',
    transaction_code: booking.booking_id || booking.id || booking._id || courtLabel,
    name: booking.customer_name || booking.user?.name || 'Guest',
    customer_name: booking.customer_name || booking.user?.name || 'Guest',
    contact_number:
      booking.customer_phone || booking.user?.phone || booking.customer_email || booking.user?.email || '-',
    customer_phone: booking.customer_phone,
    booking_infos: [bookingInfo],
    payment_status: effectivePaymentStatus,
    original_payment_status: booking.payment_status,
    payment_method: booking.payment_method || booking.transaction_type || 'walk_in',
    payment_type: booking.transaction_type || 'walk_in',
    total_amount: booking.total_amount || 0,
    status: isCancelled ? 'cancelled' : (isExpired ? 'expired' : booking.status),
    original_status: booking.status,
    is_expired: isExpired,
    is_cancelled: isCancelled,
    cancellation_reason: booking.cancellation_reason,
    cancelled_at: booking.cancelled_at,
    expired_at: booking.expired_at,
    created_at: booking.booked_at || booking.created_at || booking.updated_at || booking.date,
    date: booking.date,
    is_special_package: Boolean(booking.is_special_package),
    special_package_name: booking.special_package_name,
    special_package_id: booking.special_package_id,
    special_package_price: booking.special_package_price,
    check_in_date: booking.check_in_date,
    check_out_date: booking.check_out_date,
    notes: booking.notes,
    add_ons: booking.add_ons || [],
    equipment_rentals: booking.equipment_rentals || [],
  };
};

const AdminTransactionsPage = () => {
  const navigate = useNavigate();
  const { isDarkMode, theme } = useAdminTheme();
  const { token, admin } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'web', 'kiosk', 'admin'
  const [statusFilter, setStatusFilter] = useState('active'); // 'all', 'active', 'paid', 'unpaid', 'expired', 'cancelled'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [addOns, setAddOns] = useState([]); // Add-ons master data
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [allTransactions, setAllTransactions] = useState([]); // Store all transactions for filtering
  const limit = 25;
  
  // Modal state
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [confirmingTransaction, setConfirmingTransaction] = useState(false);
  const [amountPaid, setAmountPaid] = useState('');
  const [changeAmount, setChangeAmount] = useState(null);
  const amountInputRef = useRef(null);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTransactionForDelete, setSelectedTransactionForDelete] = useState(null);
  
  // Active booking warning modal state
  const [activeBookingWarning, setActiveBookingWarning] = useState(null);
  const [activeBookingModalOpen, setActiveBookingModalOpen] = useState(false);

  const normalizeStatus = (s) => {
    const st = String(s || '').toLowerCase();
    if (st === 'pending') return 'reserved';
    if (st === 'cancelled') return 'cancelled';
    if (st === 'expired') return 'expired';
    return st;
  };

  const statusClassFor = (st) => {
    if (st === 'paid' || st === 'confirmed') return 'text-emerald-500';
    if (st === 'reserved' || st === 'unpaid') return 'text-amber-500';
    if (st === 'cancelled') return 'text-red-500';
    if (st === 'expired') return `${isDarkMode ? 'text-slate-400' : 'text-gray-500'} line-through`;
    if (st === 'failed') return 'text-red-500';
    return isDarkMode ? 'text-slate-200' : 'text-gray-800';
  };

  // getKioskToken is imported from shared kioskTokenManager

  const fetchTransactions = async (currentPage = page, currentLimit = limit) => {
    setLoading(true);
    setError('');

    try {
      let allTransactions = [];
      const includeWeb = sourceFilter === 'all' || sourceFilter === 'web';
      const includeKiosk = sourceFilter === 'all' || sourceFilter === 'kiosk';
      const includeAdmin = sourceFilter === 'all' || sourceFilter === 'admin';

      // Build date query parameters
      const dateParams = new URLSearchParams();
      if (dateFrom) dateParams.append('start_date', dateFrom);
      if (dateTo) dateParams.append('end_date', dateTo);
      const dateQueryString = dateParams.toString();

      if (includeWeb) {
        try {
          const webUrl = `${API}/admin/all-transactions?source=web${dateQueryString ? '&' + dateQueryString : ''}`;
          const webRes = await fetch(webUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (webRes.ok) {
            const webData = await webRes.json();
            const webTx = webData.transactions || [];
            allTransactions = [...allTransactions, ...webTx];
          }
        } catch (e) {
          console.warn('Failed to fetch web transactions:', e);
          if (sourceFilter === 'web') {
            setError('Failed to fetch web transactions');
          }
        }
      }

      if (includeAdmin) {
        try {
          const adminUrl = `${API}/admin/bookings?limit=1000${dateQueryString ? '&' + dateQueryString : ''}`;
          const adminRes = await fetch(adminUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (adminRes.ok) {
            const adminData = await adminRes.json();
            const adminBookings = (adminData.bookings || [])
              .filter((booking) =>
                booking?.booking_type === 'walk_in' ||
                booking?.is_special_package ||
                booking?.source === 'block_off' ||  // Include block-offs as admin reservations
                Boolean(booking?.created_by || booking?.created_by_admin)
              )
              .map(mapBookingToAdminTransaction)
              .filter(Boolean);
            allTransactions = [...allTransactions, ...adminBookings];
          }
        } catch (e) {
          console.warn('Failed to fetch admin bookings:', e);
          if (sourceFilter === 'admin') {
            setError('Failed to fetch admin bookings');
          }
        }
      }

      if (includeKiosk) {
        try {
          const kioskToken = await getKioskToken();
          if (!kioskToken) throw new Error('Could not obtain kiosk token');
          
          // Build kiosk API URL with date parameters
          let kioskUrl = KIOSK_TRANSACTIONS_URL;
          const kioskParams = new URLSearchParams();
          if (dateFrom) kioskParams.append('start_date', dateFrom);
          if (dateTo) kioskParams.append('end_date', dateTo);
          const kioskQueryString = kioskParams.toString();
          if (kioskQueryString) {
            kioskUrl += '?' + kioskQueryString;
          }
          
          const res = await fetch(kioskUrl, {
            headers: {
              'X-KIOSK-TOKEN': kioskToken,
            },
          });

          if (res.ok) {
            const responseData = await res.json();
            let txArray = Array.isArray(responseData?.data) ? responseData.data : [];
            
            // Apply client-side date range filtering for kiosk transactions
            // (since kiosk API may not support date filtering)
            if (dateFrom || dateTo) {
              console.log('Filtering kiosk transactions by date range:', { dateFrom, dateTo });
              txArray = txArray.filter(tx => {
                const txDate = tx.created_at || tx.date;
                if (!txDate) {
                  console.log('Kiosk transaction has no date:', tx);
                  return true; // Include if no date available
                }
                
                const transactionDate = new Date(txDate);
                const fromDate = dateFrom ? new Date(dateFrom) : null;
                const toDate = dateTo ? new Date(dateTo) : null;
                
                // Set time to end of day for "to" date to include the entire day
                if (toDate) {
                  toDate.setHours(23, 59, 59, 999);
                }
                
                const withinRange = (!fromDate || transactionDate >= fromDate) && (!toDate || transactionDate <= toDate);
                console.log('Kiosk transaction date check:', {
                  txDate,
                  transactionDate,
                  fromDate,
                  toDate,
                  withinRange
                });
                
                return withinRange;
              });
            }
            
            const kioskTx = txArray.map((tx) => ({ ...tx, source: 'kiosk' }));
            allTransactions = [...allTransactions, ...kioskTx];
          }
        } catch (e) {
          console.warn('Failed to fetch kiosk transactions:', e);
          if (sourceFilter === 'kiosk') {
            setError('Failed to fetch kiosk transactions');
          }
        }
      }

      setAllTransactions(allTransactions);
    } catch (e) {
      console.error('Failed to fetch transactions:', e);
      setTransactions([]);
      setError(e.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const viewTransactionDetails = async (transaction) => {
    setModalOpen(true);
    setModalLoading(true);
    setSelectedTransaction(null);
    setAmountPaid('');
    setChangeAmount(null);

    try {
      // Web and admin transactions already contain full details
      if (transaction.source === 'web' || transaction.source === 'admin') {
        setSelectedTransaction(transaction);
        setModalLoading(false);
        return;
      }

      // For kiosk transactions, fetch details from kiosk API
      const kioskToken = await getKioskToken();
      if (!kioskToken) throw new Error('Could not obtain kiosk token');
      const url = `${KIOSK_API_BASE}/transactions/${transaction.transaction_code}`;
      
      const res = await fetch(url, {
        headers: { 
          'X-KIOSK-TOKEN': kioskToken,
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch transaction details: ${res.status}`);
      }

      const responseData = await res.json();
      console.log('Transaction details response:', responseData);
      // Response format: { data: {...}, message: "..." }
      const txData = responseData?.data || responseData;
      txData.source = 'kiosk'; // Mark as kiosk transaction
      setSelectedTransaction(txData);
      // focus will be handled by effect when selectedTransaction is set
    } catch (e) {
      console.error('Failed to fetch transaction details:', e);
      toast.error(`Failed to load transaction: ${e.message}`);
      setModalOpen(false);
    } finally {
      setModalLoading(false);
    }
  };

  // When the modal opens and the transaction details are present, focus the amount input.
  useEffect(() => {
    if (modalOpen && !modalLoading && selectedTransaction && amountInputRef.current) {
      try {
        amountInputRef.current.focus();
        amountInputRef.current.select && amountInputRef.current.select();
      } catch (err) {
        // ignore focus errors
      }
    }
  }, [modalOpen, modalLoading, selectedTransaction]);

  const calculateChange = () => {
    if (!selectedTransaction || !amountPaid) {
      toast.error('Please enter amount paid');
      return;
    }

    const totalAmount = Number(selectedTransaction.total_amount || 0);
    const paid = Number(amountPaid);

    if (isNaN(paid) || paid <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const change = paid - totalAmount;
    setChangeAmount(change);

    if (change < 0) {
      toast.error('Insufficient payment amount');
    }
  };

  const confirmTransaction = async () => {
    if (confirmingTransaction) return; // Prevent multiple calls
    
    if (!selectedTransaction?.transaction_code) {
      toast.error('No transaction selected');
      return;
    }

    setConfirmingTransaction(true);
    try {
      const kioskToken = await getKioskToken();
      if (!kioskToken) throw new Error('Could not obtain kiosk token');
      const url = `${KIOSK_API_BASE}/transactions/${selectedTransaction.transaction_code}/confirm`;
      
      const res = await fetch(url, {
        method: 'POST',
        headers: { 
          'X-KIOSK-TOKEN': kioskToken,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to confirm transaction: ${res.status}`);
      }

      const responseData = await res.json();
      
      // Save to MongoDB database
      const { token } = JSON.parse(localStorage.getItem('adminAuth') || '{}');
      const summary = formatBookingSummary(selectedTransaction.booking_infos);
      
      const transactionRecord = {
        transaction_code: selectedTransaction.transaction_code,
        customer_name: selectedTransaction.name || '-',
        contact: selectedTransaction.contact_number || '-',
        court: summary.courts.join(', '),
        players: summary.players.join(', '),
        booking_date: summary.dates.join(', '),
        time: summary.times.join(', '),
        payment_type: selectedTransaction.payment_type || '-',
        payment_status: 'paid',
        total: Number(selectedTransaction.total_amount || 0),
        change_amount: changeAmount
      };
      
      const saveResponse = await fetch(`${BACKEND_API_BASE}/api/admin/kiosk-transactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(transactionRecord)
      });
      
      if (!saveResponse.ok) {
        const errorData = await saveResponse.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to save transaction to database:', errorData);
        toast.warning('Transaction confirmed but not saved to database');
      }

      // Create bookings for each booking_info
      const bookingInfos = selectedTransaction.booking_infos || [];
      for (const info of bookingInfos) {
        if (!info.court_id || !info.booking_date || !info.time_slots) continue;

        try {
          // Calculate start and end times from time_slots
          const timeSlots = info.time_slots || [];
          const startTime = timeSlots.length > 0 ? timeSlots[0] : '08:00';
          const lastSlot = timeSlots.length > 0 ? timeSlots[timeSlots.length - 1] : '09:00';
          const endHour = parseInt(lastSlot.split(':')[0]) + 1;
          const endTime = `${String(endHour).padStart(2, '0')}:00`;

          const bookingPayload = {
            court_id: info.court_id,
            date: info.booking_date,
            start_time: startTime,
            end_time: endTime,
            customer_name: selectedTransaction.name || 'Kiosk Customer',
            customer_email: selectedTransaction.email || '',
            customer_phone: info.contact_no || selectedTransaction.contact_number || '',
            duration_hours: timeSlots.length,
            total_amount: Number(selectedTransaction.total_amount || 0) / bookingInfos.length,
            payment_method: 'cash',
            payment_status: 'paid',
            notes: `Kiosk Transaction: ${selectedTransaction.transaction_code}`,
            add_ons: []
          };

          const bookingRes = await fetch(`${BACKEND_API_BASE}/api/admin/bookings/walk-in`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(bookingPayload)
          });

          if (!bookingRes.ok) {
            const bookingError = await bookingRes.json().catch(() => ({ detail: 'Unknown error' }));
            console.error(`Failed to create booking for court ${info.court_id}:`, bookingError);
          }
        } catch (bookingError) {
          console.error('Error creating booking:', bookingError);
        }
      }
      
      toast.success('Transaction confirmed and saved successfully');
      
      // Update the transaction in the list
      setTransactions(transactions.map(t => 
        t.transaction_code === selectedTransaction.transaction_code 
          ? { ...t, payment_status: 'paid' }
          : t
      ));
      
      // Update selected transaction
      setSelectedTransaction({ ...selectedTransaction, payment_status: 'paid' });
      
      // Close modal after a delay
      setTimeout(() => {
        closeModal();
      }, 1500);
    } catch (e) {
      console.error('Failed to confirm transaction:', e);
      toast.error(`Failed to confirm: ${e.message}`);
    } finally {
      setConfirmingTransaction(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedTransaction(null);
    setAmountPaid('');
    setChangeAmount(null);
  };

  const openDeleteModal = (transaction) => {
    setSelectedTransactionForDelete(transaction);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedTransactionForDelete(null);
  };

  const deleteTransaction = async () => {
    if (!selectedTransactionForDelete) return;

    const txCode = selectedTransactionForDelete.transaction_code;
    const bookingDate = selectedTransactionForDelete.date || 
                        selectedTransactionForDelete.booking_infos?.[0]?.booking_date || 
                        '';

    // For block-offs and admin reservations, redirect directly to bookings page
    // Block-offs have transaction codes starting with "block_" and are stored in a different collection
    if (txCode?.startsWith('block_') || selectedTransactionForDelete.source === 'block_off') {
      closeDeleteModal();
      navigate(`/admin/bookings?date=${bookingDate}&highlight=${txCode}&action=delete`);
      toast.info('Redirecting to booking for deletion confirmation...', { duration: 2000 });
      return;
    }

    // For admin bookings (book_ prefix), also redirect to bookings page
    if (txCode?.startsWith('book_') && selectedTransactionForDelete.source === 'admin') {
      closeDeleteModal();
      navigate(`/admin/bookings?date=${bookingDate}&highlight=${txCode}&action=delete`);
      toast.info('Redirecting to booking for deletion confirmation...', { duration: 2000 });
      return;
    }

    try {
      const response = await fetch(`${API}/admin/transactions/${txCode}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Clone response before reading to avoid "body stream already read" error
      const responseClone = response.clone();
      let errorData = null;
      
      try {
        errorData = await response.json();
      } catch (parseError) {
        // Response is not JSON, try reading as text from clone
        try {
          const textBody = await responseClone.text();
          console.warn('Non-JSON response:', textBody);
        } catch (e) {
          // Ignore
        }
      }

      if (!response.ok) {
        // Check if this is an active booking error (409 Conflict)
        if (response.status === 409 && errorData?.detail?.error === 'active_booking') {
          // Close delete modal and redirect to bookings page with delete action
          closeDeleteModal();
          const bookingId = errorData.detail.booking_id;
          const bookingInfoDate = errorData.detail.booking_info?.date || '';
          
          // Navigate to bookings page with delete action parameter
          navigate(`/admin/bookings?date=${bookingInfoDate}&highlight=${bookingId}&action=delete`);
          
          toast.info('Redirecting to booking details for confirmation...', { duration: 2000 });
          return;
        }

        // For 404, the booking might be in the bookings collection - redirect there
        if (response.status === 404) {
          closeDeleteModal();
          navigate(`/admin/bookings?date=${bookingDate}&highlight=${txCode}&action=delete`);
          toast.info('Redirecting to booking for deletion confirmation...', { duration: 2000 });
          return;
        }
        
        throw new Error(errorData?.detail?.message || errorData?.detail || 'Failed to delete transaction');
      }

      toast.success('Transaction deleted successfully');
      closeDeleteModal();
      fetchTransactions(); // Refresh the list
    } catch (e) {
      console.error('Failed to delete transaction:', e);
      toast.error(`Failed to delete: ${e.message}`);
    }
  };

  const handleGoToBooking = () => {
    if (activeBookingWarning?.redirect_url) {
      navigate(activeBookingWarning.redirect_url);
    } else if (activeBookingWarning?.booking_info?.date) {
      navigate(`/admin/bookings?date=${activeBookingWarning.booking_info.date}`);
    } else {
      navigate('/admin/bookings');
    }
    setActiveBookingModalOpen(false);
    setActiveBookingWarning(null);
  };

  const closeActiveBookingModal = () => {
    setActiveBookingModalOpen(false);
    setActiveBookingWarning(null);
  };

  useEffect(() => {
    if (token) {
      setPage(1); // Reset to page 1 when filters change
      fetchTransactions(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFilter, dateFrom, dateTo, token]);

  // Initial fetch
  useEffect(() => {
    if (token) {
      fetchTransactions();
    }
  }, [token]);

  // Fetch when page changes (but not when filters change, since that resets page)
  useEffect(() => {
    if (token && page > 1) {
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, token]);

  // Fetch add-ons master data
  useEffect(() => {
    const fetchAddOns = async () => {
      try {
        const res = await fetch(`${API}/admin/pos/items`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (res.ok) {
          const addOnsData = await res.json();
          console.log('Fetched addOns data:', addOnsData);
          setAddOns(addOnsData);
        }
      } catch (error) {
        console.warn('Failed to fetch add-ons:', error);
      }
    };

    if (token) {
      fetchAddOns();
    }
  }, [token]);

  // Filter and paginate transactions
  const filteredTransactions = React.useMemo(() => {
    let filtered = allTransactions.filter((t) => {
      // Status filter
      const paymentStatus = normalizeStatus(t.payment_status);
      const bookingStatus = normalizeStatus(t.status);
      const isExpired = t.is_expired === true || paymentStatus === 'expired' || bookingStatus === 'expired';
      const isCancelled = t.is_cancelled === true || paymentStatus === 'cancelled' || bookingStatus === 'cancelled';
      const isPaid = paymentStatus === 'paid' || paymentStatus === 'confirmed';
      const isUnpaid = ['unpaid', 'pending', 'reserved'].includes(paymentStatus);
      
      if (statusFilter === 'active') {
        // Active = not expired and not cancelled
        if (isExpired || isCancelled) return false;
      } else if (statusFilter === 'paid') {
        if (!isPaid) return false;
      } else if (statusFilter === 'unpaid') {
        if (!isUnpaid || isExpired || isCancelled) return false;
      } else if (statusFilter === 'expired') {
        if (!isExpired) return false;
      } else if (statusFilter === 'cancelled') {
        if (!isCancelled) return false;
      }
      // 'all' shows everything
      
      // Search query filter
      if (!searchQuery.trim()) return true;
      
      const query = searchQuery.toLowerCase();
      const transactionCode = String(t.transaction_code || '').toLowerCase();
      const name = String(t.name || t.customer_name || '').toLowerCase();
      const contactNumber = String(t.contact_number || t.customer_phone || '').toLowerCase();
      const notes = String(t.notes || '').toLowerCase();
      
      return transactionCode.includes(query) || 
             name.includes(query) || 
             contactNumber.includes(query) ||
             notes.includes(query);
    });

    // Apply pagination
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginated = filtered.slice(startIndex, endIndex);
    
    // Update total and totalPages
    setTotal(filtered.length);
    setTotalPages(Math.ceil(filtered.length / limit));
    
    return paginated;
  }, [allTransactions, statusFilter, searchQuery, page, limit]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <Card className={`${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}`}>
          <CardHeader className="space-y-4 pb-4">
            {/* Row 1: Title + Refresh */}
            <div className="flex items-center justify-between">
              <CardTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                <Receipt className="h-5 w-5 text-emerald-500" />
                Transactions
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className={`${isDarkMode ? 'text-slate-400' : 'text-gray-500'} text-sm hidden sm:block`}>
                  {filteredTransactions.length} {filteredTransactions.length !== total && `of ${total}`} records
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchTransactions}
                  className={`h-8 ${isDarkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-700/50' : 'border-gray-200 text-gray-800 hover:bg-gray-50'}`}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {/* Row 2: Filters */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Date Range Filter */}
              <div className="flex items-center gap-1.5">
                <Label className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>From:</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className={`w-28 h-7 text-xs ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300'}`}
                />
                <Label className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>To:</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className={`w-28 h-7 text-xs ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300'}`}
                />
                {(dateFrom || dateTo) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDateFrom('');
                      setDateTo('');
                    }}
                    className={`h-7 px-1.5 text-xs ${isDarkMode ? 'text-slate-400 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>

              {/* Divider */}
              <div className={`hidden sm:block h-6 w-px ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`} />

              {/* Source Filter */}
              <div className="flex items-center gap-0.5 p-0.5 rounded-md bg-slate-100">
                <Button
                  variant={sourceFilter === 'all' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSourceFilter('all')}
                  className="h-6 px-2 text-xs"
                >
                  All
                </Button>
                <Button
                  variant={sourceFilter === 'admin' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSourceFilter('admin')}
                  className="h-6 px-2 text-xs"
                >
                  <Shield className="h-3 w-3 mr-0.5" />
                  Admin
                </Button>
                <Button
                  variant={sourceFilter === 'web' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSourceFilter('web')}
                  className="h-6 px-2 text-xs"
                >
                  <Globe className="h-3 w-3 mr-0.5" />
                  Web
                </Button>
                <Button
                  variant={sourceFilter === 'kiosk' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSourceFilter('kiosk')}
                  className="h-6 px-2 text-xs"
                >
                  <Monitor className="h-3 w-3 mr-0.5" />
                  Kiosk
                </Button>
              </div>

              {/* Divider */}
              <div className={`hidden sm:block h-6 w-px ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`} />

              {/* Status Filter */}
              <div className="flex items-center gap-0.5 p-0.5 rounded-md bg-slate-100">
                <Button
                  variant={statusFilter === 'active' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setStatusFilter('active')}
                  className="h-6 px-2 text-xs"
                >
                  Active
                </Button>
                <Button
                  variant={statusFilter === 'all' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setStatusFilter('all')}
                  className="h-6 px-2 text-xs"
                >
                  All
                </Button>
                <Button
                  variant={statusFilter === 'paid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setStatusFilter('paid')}
                  className="h-6 px-2 text-xs text-emerald-600"
                >
                  Paid
                </Button>
                <Button
                  variant={statusFilter === 'unpaid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setStatusFilter('unpaid')}
                  className="h-6 px-2 text-xs text-amber-600"
                >
                  Unpaid
                </Button>
                <Button
                  variant={statusFilter === 'expired' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setStatusFilter('expired')}
                  className="h-6 px-2 text-xs text-gray-500"
                >
                  Expired
                </Button>
              </div>

              {/* Divider */}
              <div className={`hidden sm:block h-6 w-px ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`} />

              {/* Search Input */}
              <div className="relative flex-1 min-w-[180px] max-w-xs">
                <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`pl-8 h-7 text-xs ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white placeholder:text-slate-500' : 'bg-white border-gray-300 placeholder:text-gray-400'}`}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
              </div>
            ) : error ? (
              <p className={`${isDarkMode ? 'text-rose-300' : 'text-rose-600'} text-center py-8`}>{error}</p>
            ) : filteredTransactions.length === 0 ? (
              <p className={`${isDarkMode ? 'text-slate-500' : 'text-gray-500'} text-center py-8`}>
                {searchQuery ? 'No transactions match your search' : 'No transactions found'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`text-left text-sm ${isDarkMode ? 'text-slate-400 border-slate-700' : 'text-gray-500 border-gray-200'} border-b`}>
                      <th className="pb-3 font-medium">#</th>
                      <th className="pb-3 font-medium">Source</th>
                      <th className="pb-3 font-medium">Transaction Code</th>
                      <th className="pb-3 font-medium">Customer</th>
                      <th className="pb-3 font-medium">Contact</th>
                      <th className="pb-3 font-medium">Court</th>
                      <th className="pb-3 font-medium">Players</th>
                      <th className="pb-3 font-medium">Play Date</th>
                      <th className="pb-3 font-medium">Time</th>
                      <th className="pb-3 font-medium hidden lg:table-cell">Add-ons</th>
                      <th className="pb-3 font-medium hidden xl:table-cell">Transaction Date</th>
                      <th className="pb-3 font-medium">Payment Status</th>
                      <th className="pb-3 font-medium text-right">Total</th>
                      <th className="pb-3 font-medium text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {filteredTransactions.map((t, idx) => {
                      // Handle both web, admin, and kiosk transaction formats
                      const isWebTransaction = t.source === 'web';
                      const isAdminTransaction = t.source === 'admin';
                      const summary = isWebTransaction 
                        ? {
                            courts: [t.court_name || `Court ${t.court_id}`],
                            dates: [t.booking_date ? formatDateMMDDYYYY(t.booking_date) : '-'],
                            times: [t.start_time && t.end_time ? `${formatKioskTimeDisplay(t.start_time)} - ${formatKioskTimeDisplay(t.end_time)}` : '-'],
                            players: [t.num_players || t.player_count || '-']
                          }
                        : formatBookingSummary(t.booking_infos);
                      const SourceIcon = isWebTransaction ? Globe : isAdminTransaction ? Shield : Monitor;
                      const sourceBadgeClasses = isWebTransaction
                        ? 'bg-blue-100 text-blue-700 border-blue-300'
                        : isAdminTransaction
                          ? 'bg-amber-100 text-amber-700 border-amber-300'
                          : 'bg-purple-100 text-purple-700 border-purple-300';
                      const sourceLabel = isWebTransaction ? 'Web' : isAdminTransaction ? 'Admin' : 'Kiosk';
                      const rawStatus = String(t.payment_status || t.status || '').toLowerCase();
                      // Show "pending" as "reserved" in the UI; color reserved (and unpaid) as orange
                      const paymentStatus = rawStatus === 'pending' ? 'reserved' : rawStatus;
                      const statusClass =
                        paymentStatus === 'paid' || paymentStatus === 'confirmed'
                          ? 'text-emerald-500'
                          : paymentStatus === 'reserved' || paymentStatus === 'unpaid'
                          ? 'text-amber-500'
                          : isDarkMode
                          ? 'text-slate-200'
                          : 'text-gray-800';

                      return (
                        <tr
                          key={t.id || t.transaction_id || t.transaction_code || idx}
                          className={`${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'} border-b`}
                        >
                          <td className={`py-3 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{idx + 1}</td>
                          <td className="py-3">
                            <Badge 
                              variant="outline" 
                              className={sourceBadgeClasses}
                            >
                              <SourceIcon className="h-3 w-3 mr-1" />
                              {sourceLabel}
                            </Badge>
                          </td>
                          <td className={`py-3 font-mono text-xs ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                            {t.transaction_code || '-'}
                          </td>
                          <td className={`py-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {t.name || t.customer_name || '-'}
                          </td>
                          <td className={`py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                            {t.contact_number || t.customer_phone || '-'}
                          </td>
                          <td className={`py-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                            <div className="space-y-1">
                              {summary.courts.map((value, i) => (
                                <div key={`${t.id || t.transaction_code || idx}-court-${i}`}>{value}</div>
                              ))}
                            </div>
                          </td>
                          <td className={`py-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                            <div className="space-y-1">
                              {summary.players.map((value, i) => (
                                <div key={`${t.id || t.transaction_code || idx}-players-${i}`}>{value}</div>
                              ))}
                            </div>
                          </td>
                          <td className={`py-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                            <div className="space-y-1">
                              {summary.dates.map((value, i) => (
                                <div key={`${t.id || t.transaction_code || idx}-date-${i}`}>{value}</div>
                              ))}
                            </div>
                          </td>
                          <td className={`py-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                            <div className="space-y-1">
                              {summary.times.map((value, i) => (
                                <div key={`${t.id || t.transaction_code || idx}-time-${i}`}>{value}</div>
                              ))}
                            </div>
                          </td>
                          <td className={`py-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'} hidden lg:table-cell`}>
                            {(t.add_ons && t.add_ons.length > 0) || (t.equipment_rentals && t.equipment_rentals.length > 0) ? 'Yes' : 'No'}
                          </td>
                          <td className={`py-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'} hidden xl:table-cell`}>
                            {t.created_at ? formatDateMMDDYYYY(new Date(t.created_at).toISOString().split('T')[0]) : '-'}
                          </td>
                          <td className={`py-3 ${statusClass}`}>{paymentStatus || '-'}</td>
                          <td className="py-3 text-right text-emerald-500">
                            ₱{Number(t.total_amount || t.amount || 0).toFixed(2)}
                          </td>
                          <td className="py-3 text-center">
                            <div className="flex gap-2 justify-center">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => viewTransactionDetails(t)}
                                className={`${isDarkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-700/50' : 'border-gray-200 text-gray-800 hover:bg-gray-50'}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {t.source === 'admin' && admin && ['super_admin', 'manager'].includes(admin.role) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openDeleteModal(t)}
                                  className={`${isDarkMode ? 'border-rose-700 text-rose-400 hover:bg-rose-700/50' : 'border-rose-200 text-rose-600 hover:bg-rose-50'}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700/50">
                <p className={theme.textMuted}>
                  Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} records
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={isDarkMode ? 'border-slate-600' : ''}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className={theme.text}>
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={isDarkMode ? 'border-slate-600' : ''}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Footer: Record count + History button */}
            <div className={`flex items-center justify-between pt-4 mt-2 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
              <div className={`${isDarkMode ? 'text-slate-400' : 'text-gray-500'} text-sm sm:hidden`}>
                {filteredTransactions.length} {filteredTransactions.length !== total && `of ${total}`} records
              </div>
              <div className={`${isDarkMode ? 'text-slate-400' : 'text-gray-500'} text-sm hidden sm:block`}>
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} transaction{total !== 1 ? 's' : ''}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin/booking-history')}
                className={`${isDarkMode ? 'border-slate-700 text-amber-400 hover:bg-slate-700/50 hover:text-amber-300' : 'border-gray-200 text-amber-600 hover:bg-amber-50 hover:text-amber-700'}`}
              >
                <History className="h-4 w-4 mr-2" />
                Booking History
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Transaction Details Modal */}
        <Dialog open={modalOpen} onOpenChange={closeModal}>
          <DialogContent className={`max-w-2xl w-full mx-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <DialogHeader>
              <DialogTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                <Receipt className="h-5 w-5 text-emerald-500" />
                Transaction Summary
                {selectedTransaction?.source && (
                  <Badge 
                    variant="outline" 
                    className={
                      selectedTransaction.source === 'web'
                        ? 'ml-2 bg-blue-100 text-blue-700 border-blue-300'
                        : selectedTransaction.source === 'admin'
                          ? 'ml-2 bg-amber-100 text-amber-700 border-amber-300'
                          : 'ml-2 bg-purple-100 text-purple-700 border-purple-300'
                    }
                  >
                    {selectedTransaction.source === 'web'
                      ? 'Web Booking'
                      : selectedTransaction.source === 'admin'
                        ? 'Admin Booking'
                        : 'Kiosk'}
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>

            {modalLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
              </div>
            ) : selectedTransaction ? (
              <div className="max-h-[70vh] overflow-y-auto space-y-6">
                {/* Status Banner for Expired/Cancelled */}
                {(selectedTransaction.is_expired || selectedTransaction.is_cancelled || 
                  selectedTransaction.status === 'expired' || selectedTransaction.status === 'cancelled') && (
                  <div className={`p-3 rounded-lg flex items-center gap-3 ${
                    selectedTransaction.is_cancelled || selectedTransaction.status === 'cancelled'
                      ? (isDarkMode ? 'bg-red-900/30 border border-red-800/50' : 'bg-red-50 border border-red-200')
                      : (isDarkMode ? 'bg-gray-700/50 border border-gray-600' : 'bg-gray-100 border border-gray-300')
                  }`}>
                    <AlertTriangle className={`h-5 w-5 ${
                      selectedTransaction.is_cancelled || selectedTransaction.status === 'cancelled' 
                        ? 'text-red-500' 
                        : 'text-gray-500'
                    }`} />
                    <div>
                      <p className={`font-medium ${
                        selectedTransaction.is_cancelled || selectedTransaction.status === 'cancelled'
                          ? (isDarkMode ? 'text-red-300' : 'text-red-700')
                          : (isDarkMode ? 'text-gray-300' : 'text-gray-700')
                      }`}>
                        {selectedTransaction.is_cancelled || selectedTransaction.status === 'cancelled' 
                          ? 'This booking has been cancelled'
                          : 'This booking has expired (past date with unpaid status)'}
                      </p>
                      {selectedTransaction.cancellation_reason && (
                        <p className={`text-sm mt-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                          Reason: {selectedTransaction.cancellation_reason}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Transaction Info */}
                <div className={`grid grid-cols-2 gap-4 p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Transaction Code</Label>
                    <p className={`font-mono text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedTransaction.transaction_code || '-'}
                    </p>
                  </div>
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Created</Label>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedTransaction.created_at ? new Date(selectedTransaction.created_at).toLocaleString() : '-'}
                    </p>
                  </div>
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Customer</Label>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedTransaction.name || selectedTransaction.customer_name || '-'}
                    </p>
                  </div>
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Contact</Label>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedTransaction.contact_number || selectedTransaction.customer_phone || selectedTransaction.customer_email || '-'}
                    </p>
                  </div>
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Booking Status</Label>
                    <Badge 
                      variant="outline" 
                      className={
                        selectedTransaction.status === 'cancelled'
                          ? 'bg-red-100 text-red-700 border-red-300'
                          : selectedTransaction.status === 'expired' || selectedTransaction.is_expired
                            ? 'bg-gray-100 text-gray-700 border-gray-300'
                            : selectedTransaction.status === 'confirmed'
                              ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                              : 'bg-amber-100 text-amber-700 border-amber-300'
                      }
                    >
                      {selectedTransaction.status || 'pending'}
                    </Badge>
                  </div>
                  <div>
                    <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Booking Date</Label>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedTransaction.date ? formatDateMMDDYYYY(selectedTransaction.date) : '-'}
                    </p>
                  </div>
                </div>

                {/* Notes Section */}
                {selectedTransaction.notes && (
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/20 border border-blue-800/40' : 'bg-blue-50 border border-blue-200'}`}>
                    <Label className={`text-sm mb-2 block ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      <FileText className="h-4 w-4 inline mr-2" />
                      Notes
                    </Label>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                      {selectedTransaction.notes}
                    </p>
                  </div>
                )}

                {/* Booking Details for Kiosk Transactions */}
                {selectedTransaction.booking_infos && selectedTransaction.booking_infos.length > 0 && (
                  <div>
                    <Label className={`text-sm mb-2 block ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Booking Details</Label>
                    <div className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                      <table className="w-full text-sm">
                        <thead className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                          <tr>
                            <th className={`px-3 py-2 text-left ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Court</th>
                            <th className={`px-3 py-2 text-left ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Date</th>
                            <th className={`px-3 py-2 text-left ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Time</th>
                            <th className={`px-3 py-2 text-left ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Players</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTransaction.booking_infos.map((info, idx) => (
                            <tr key={idx} className={`${isDarkMode ? 'border-slate-700' : 'border-gray-200'} border-t`}>
                              <td className={`px-3 py-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {info?.court_no != null ? `Court ${info.court_no}` : info?.court || '-'}
                              </td>
                              <td className={`px-3 py-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {info?.booking_date || '-'}
                              </td>
                              <td className={`px-3 py-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {info?.booking_time_start && info?.booking_time_end 
                                  ? `${formatKioskTimeDisplay(info.booking_time_start)} - ${formatKioskTimeDisplay(info.booking_time_end)}`
                                  : info?.time_slot ? formatKioskTimeDisplay(info.time_slot) : '-'}
                              </td>
                              <td className={`px-3 py-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {info?.player_count || info?.num_players || info?.players || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Booking Details for Web Transactions */}
                {selectedTransaction.source === 'web' && !selectedTransaction.booking_infos && (
                  <div>
                    <Label className={`text-sm mb-2 block ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Booking Details</Label>
                    <div className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                      <table className="w-full text-sm">
                        <thead className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                          <tr>
                            <th className={`px-3 py-2 text-left ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Court</th>
                            <th className={`px-3 py-2 text-left ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Date</th>
                            <th className={`px-3 py-2 text-left ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Time</th>
                            <th className={`px-3 py-2 text-left ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Duration</th>
                            <th className={`px-3 py-2 text-left ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Players</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className={`${isDarkMode ? 'border-slate-700' : 'border-gray-200'} border-t`}>
                            <td className={`px-3 py-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {selectedTransaction.court_name || `Court ${selectedTransaction.court_id}` || '-'}
                            </td>
                            <td className={`px-3 py-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {selectedTransaction.booking_date || '-'}
                            </td>
                            <td className={`px-3 py-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {selectedTransaction.start_time && selectedTransaction.end_time 
                                ? `${formatKioskTimeDisplay(selectedTransaction.start_time)} - ${formatKioskTimeDisplay(selectedTransaction.end_time)}`
                                : '-'}
                            </td>
                            <td className={`px-3 py-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {selectedTransaction.duration_hours ? `${selectedTransaction.duration_hours} hour(s)` : '-'}
                            </td>
                            <td className={`px-3 py-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {selectedTransaction.num_players || selectedTransaction.player_count || '-'}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {selectedTransaction.source === 'admin' && selectedTransaction.is_special_package && (
                  <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-amber-900/20 border-amber-800/40' : 'bg-amber-50 border-amber-200'} space-y-3`}>
                    <div className="flex items-center justify-between">
                      <Label className={`text-sm ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>Package</Label>
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedTransaction.special_package_name || 'Special Package'}
                      </span>
                    </div>
                    {selectedTransaction.special_package_price != null && (
                      <div className="flex items-center justify-between">
                        <Label className={`text-sm ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>Package Price</Label>
                        <span className={`text-sm font-semibold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          ₱{Number(selectedTransaction.special_package_price).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {(selectedTransaction.check_in_date || selectedTransaction.check_out_date) && (
                      <div className="flex items-center justify-between">
                        <Label className={`text-sm ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>Stay</Label>
                        <span className={`text-sm ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                          {selectedTransaction.check_in_date || 'TBD'}
                          <span className={`mx-2 ${isDarkMode ? 'text-amber-300/70' : 'text-amber-700/70'}`}>
                            →
                          </span>
                          {selectedTransaction.check_out_date || 'TBD'}
                        </span>
                      </div>
                    )}
                    {selectedTransaction.notes && (
                      <div>
                        <Label className={`text-sm mb-1 ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>Notes</Label>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                          {selectedTransaction.notes}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Add-ons Section */}
                {((selectedTransaction.add_ons && selectedTransaction.add_ons.length > 0) || 
                  (selectedTransaction.equipment_rentals && selectedTransaction.equipment_rentals.length > 0)) && (
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                    <Label className={`text-sm mb-3 block ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Add-ons</Label>
                    <div className="space-y-2">
                      {((selectedTransaction.equipment_rentals && selectedTransaction.equipment_rentals.length > 0) 
                        ? selectedTransaction.equipment_rentals 
                        : selectedTransaction.add_ons || []).map((addon, index) => {
                        const quantity = addon.quantity || 1;
                        // For equipment_rentals, name and price are included directly
                        // For add_ons, we need to look up from the addOns array
                        let name, price;
                        // Check if this addon has name/price directly (equipment_rentals format)
                        if (addon.name && addon.price != null) {
                          name = addon.name;
                          price = addon.price;
                        } else {
                          // Look up from addOns master data
                          const addonItem = addOns.find(a => a.item_id === addon.item_id);
                          name = addonItem?.name || addon.item_id;
                          price = addonItem?.price || 0;
                        }
                        return (
                          <div key={index} className="flex justify-between">
                            <span className={`${isDarkMode ? 'text-slate-300' : 'text-gray-700'} text-sm`}>
                              {name} x{quantity}
                            </span>
                            <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-sm`}>
                              ₱{Number(price * quantity).toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                      <div className={`flex justify-between pt-2 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
                        <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} font-medium text-sm`}>Add-ons Total</span>
                        <span className="text-emerald-500 font-semibold text-sm">
                          ₱{Number(((selectedTransaction.equipment_rentals && selectedTransaction.equipment_rentals.length > 0) 
                            ? selectedTransaction.equipment_rentals 
                            : selectedTransaction.add_ons || []).reduce((sum, addon) => {
                            let price = 0;
                            // Check if this addon has price directly (equipment_rentals format)
                            if (addon.price != null) {
                              price = addon.price || 0;
                            } else {
                              // From add_ons - look up price
                              const addonItem = addOns.find(a => a.item_id === addon.item_id);
                              price = addonItem?.price || 0;
                            }
                            return sum + (price * (addon.quantity || 1));
                          }, 0)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Info */}
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Transaction Date</Label>
                      <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedTransaction.created_at ? formatDateMMDDYYYY(new Date(selectedTransaction.created_at).toISOString().split('T')[0]) : '-'}
                      </p>
                    </div>
                    <div>
                      <Label className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Transaction Type</Label>
                      <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedTransaction.transaction_type || (selectedTransaction.source === 'web' ? 'Online Booking' : 'Kiosk')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Payment Calculation */}
                <div className={`p-4 rounded-lg border-2 ${isDarkMode ? 'bg-slate-700/30 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'}`}>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Label className={`text-base ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Total Amount</Label>
                      <span className={`text-2xl font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        ₱{Number(selectedTransaction.total_amount || selectedTransaction.amount || 0).toFixed(2)}
                      </span>
                    </div>

                    {/* Payment Status Badge */}
                    <div className="flex justify-between items-center">
                      <Label className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Payment Status</Label>
                      <Badge 
                        variant="outline" 
                        className={
                          (selectedTransaction.payment_status === 'paid' || selectedTransaction.status === 'confirmed')
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                            : 'bg-amber-100 text-amber-700 border-amber-300'
                        }
                      >
                        {selectedTransaction.payment_status || selectedTransaction.status || 'pending'}
                      </Badge>
                    </div>

                    {/* Only show payment input for unpaid kiosk transactions */}
                    {selectedTransaction.source !== 'web' && selectedTransaction.payment_status !== 'paid' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="amountPaid" className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                            Amount Paid by Customer
                          </Label>
                          <div className="flex gap-2">
                            <Input
                              id="amountPaid"
                              type="number"
                              step="0.01"
                              placeholder="Enter amount"
                              value={amountPaid}
                              onChange={(e) => setAmountPaid(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') calculateChange();
                              }}
                              className={`flex-1 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                              ref={amountInputRef}
                            />
                            <Button
                              onClick={calculateChange}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              Calculate
                            </Button>
                          </div>
                        </div>

                        {changeAmount !== null && (
                          <div className={`mt-4 p-3 rounded-lg ${changeAmount >= 0 ? (isDarkMode ? 'bg-emerald-900/30' : 'bg-emerald-100') : (isDarkMode ? 'bg-rose-900/30' : 'bg-rose-100')}`}>
                            <div className="flex justify-between items-center">
                              <Label className={`text-base ${changeAmount >= 0 ? (isDarkMode ? 'text-emerald-300' : 'text-emerald-700') : (isDarkMode ? 'text-rose-300' : 'text-rose-700')}`}>
                                Change
                              </Label>
                              <span className={`text-2xl font-bold ${changeAmount >= 0 ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-600') : (isDarkMode ? 'text-rose-400' : 'text-rose-600')}`}>
                                {changeAmount >= 0 ? '₱' : '-₱'}{Math.abs(changeAmount).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Confirm Button - only for unpaid kiosk transactions */}
                {selectedTransaction.source !== 'web' && String(selectedTransaction.payment_status || '').toLowerCase() !== 'paid' && (
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                    <Button
                      variant="outline"
                      onClick={closeModal}
                      className={`${isDarkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-700/50' : 'border-gray-200 text-gray-800 hover:bg-gray-50'}`}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={confirmTransaction}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={confirmingTransaction}
                    >
                      {confirmingTransaction ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Processing...
                        </div>
                      ) : (
                        'Confirm Transaction'
                      )}
                    </Button>
                  </div>
                )}

                {String(selectedTransaction.payment_status || '').toLowerCase() === 'paid' && (
                  <div className="flex justify-end pt-4 border-t border-slate-700">
                    <Button
                      onClick={closeModal}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      Close
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <p className={`text-center py-8 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                No transaction data available
              </p>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={closeDeleteModal}>
          <DialogContent className={`max-w-md w-full mx-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <DialogHeader>
              <DialogTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                <Trash2 className="h-5 w-5 text-rose-500" />
                Delete Transaction
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto space-y-4">
              <p className={`${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                Are you sure you want to delete this transaction? This action cannot be undone.
              </p>
              {selectedTransactionForDelete && (
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                  <div className={`text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                    <strong className={`${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>Transaction Code:</strong> {selectedTransactionForDelete.transaction_code}
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                    <strong className={`${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>Customer:</strong> {selectedTransactionForDelete.name || selectedTransactionForDelete.customer_name}
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                    <strong className={`${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>Total:</strong> ₱{Number(selectedTransactionForDelete.total_amount || 0).toFixed(2)}
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={closeDeleteModal}
                  className={`${isDarkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-700/50' : 'border-gray-200 text-gray-800 hover:bg-gray-50'}`}
                >
                  Cancel
                </Button>
                <Button
                  onClick={deleteTransaction}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                >
                  Delete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Active Booking Warning Modal */}
        <Dialog open={activeBookingModalOpen} onOpenChange={closeActiveBookingModal}>
          <DialogContent className={`max-w-lg w-full mx-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <DialogHeader>
              <DialogTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Active Booking Detected
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto space-y-4">
              <div className={`p-4 rounded-lg border-2 ${isDarkMode ? 'bg-amber-900/20 border-amber-500/30' : 'bg-amber-50 border-amber-200'}`}>
                <p className={`${isDarkMode ? 'text-amber-200' : 'text-amber-800'} font-medium mb-2`}>
                  This booking is currently active and cannot be deleted directly.
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-amber-300/80' : 'text-amber-700'}`}>
                  Please manage this booking from the Bookings page. You can cancel or reschedule it there.
                </p>
              </div>
              
              {activeBookingWarning?.booking_info && (
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>Booking Details</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Customer:</span>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {activeBookingWarning.booking_info.customer_name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Court:</span>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {activeBookingWarning.booking_info.court_name || activeBookingWarning.booking_info.court_id || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Date:</span>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {activeBookingWarning.booking_info.date ? formatDateMMDDYYYY(activeBookingWarning.booking_info.date) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Time:</span>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {activeBookingWarning.booking_info.start_time || 'N/A'} - {activeBookingWarning.booking_info.end_time || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Status:</span>
                      <Badge 
                        variant="outline" 
                        className={`mt-1 ${
                          activeBookingWarning.booking_info.status === 'confirmed' 
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                            : 'bg-amber-100 text-amber-700 border-amber-300'
                        }`}
                      >
                        {activeBookingWarning.booking_info.status || 'N/A'}
                      </Badge>
                    </div>
                    <div>
                      <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Amount:</span>
                      <p className={`font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        ₱{Number(activeBookingWarning.booking_info.total_amount || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={closeActiveBookingModal}
                  className={`${isDarkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-700/50' : 'border-gray-200 text-gray-800 hover:bg-gray-50'}`}
                >
                  Close
                </Button>
                <Button
                  onClick={handleGoToBooking}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Go to Booking
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminTransactionsPage;
